import React, { useState } from 'react';

import styled from 'styled-components';

import { deleteIncentive } from 'apis/CommunityAPI';
import ConfirmModal from 'common/Modal/ConfirmModal';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import Dropdown from 'components/lib/Dropdown';
import { hasPermissionOrAdmin } from 'helpers/permissions';
import { INCENTIVES_EDIT_PERMISSIONS } from 'permissions/incentives';
import { useAppSelector } from 'store/hook';
import { CommunityIncentiveType } from 'types/incentives';

import { useCommunityIncentivesContext } from './CommunityIncentivesContext';
import { INCENTIVE_MODAL_TYPE } from './constants';
import DownCaretIcon from '../../../common/Icons/DownCaretIcon';
import { COMMUNITY_INFO_MODE } from '../../../constants/community';
import { useCommunityInfoContext } from '../CommunityInfoContext';

const Wrapper = styled.div`
  visibility: hidden;
`;

type Props = {
  item: CommunityIncentiveType;
};

const CommunityIncentivesTableActions = ({ item }: Props) => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const { communityId, setModalType, setSelectedIncentive, refetch } = useCommunityIncentivesContext();
  const { showMessage } = useRootContext();
  const { mode } = useCommunityInfoContext();

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const handleDeleteIncentive = async () => {
    await deleteIncentive(communityId, item.id as number)
      .then((response: any) => {
        refetch();
        showMessage('success', 'Community Incentive deleted successfully');
      })
      .catch((reason: any) => {
        console.error(reason);
        showMessage('error', 'Failed to delete Community Incentive');
      });
  };

  const hasEditPermission = hasPermissionOrAdmin(currentUser, INCENTIVES_EDIT_PERMISSIONS);
  const isPrivateMode = mode === COMMUNITY_INFO_MODE.PRIVATE;
  const canEditIncentives = hasEditPermission && (!isPrivateMode || !item.shared);

  const sharedIncentiveDescription =
    'This will delete the incentive.\nYou are about to save system-level changes that will affect displayed data for multiple companies and communities. Are you sure you want to do this?';
  const privateIncentiveDescription =
    'This will delete the incentive.\nYou are about to save changes that will affect displayed data your community. Are you sure you want to do this?';
  const incentiveDescription = item.shared ? sharedIncentiveDescription : privateIncentiveDescription;
  const incentiveCheckboxLabel = item.shared
    ? 'I understand that this will affect other companies and communities'
    : undefined;

  return (
    <Wrapper className={'hover-visible'}>
      <ConfirmModal
        open={openDeleteModal}
        title={'Delete Incentives'}
        description={incentiveDescription}
        confirmLabel="Delete"
        checkboxConfirmLabel={incentiveCheckboxLabel}
        onConfirm={handleDeleteIncentive}
        onCancel={() => setOpenDeleteModal(false)}
      />
      {canEditIncentives && (
        <Dropdown
          menu={{
            items: [
              {
                label: 'Edit Incentive',
                key: 'editIncentive',
                onClick: () => {
                  setSelectedIncentive(item);
                  setModalType(INCENTIVE_MODAL_TYPE.EDIT);
                },
              },
              {
                label: 'Delete Incentive',
                key: 'deleteIncentive',
                onClick: () => setOpenDeleteModal(true),
              },
            ],
          }}
        >
          <Button style={{ width: 100 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span>Actions</span>
              <DownCaretIcon />
            </div>
          </Button>
        </Dropdown>
      )}
    </Wrapper>
  );
};

export default CommunityIncentivesTableActions;
